import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { isInIframe } from '@shared/utils/isInIframe';
import queryString from '@shared/core/queryString';
import { GuestIdentificationDialogContextProvider } from './GuestIdentificationDialog.context';
import { GuestIdentificationDialog } from './GuestIdentificationDialog';
import { GuestIdentificationStickyButton } from './GuestIdentificationStickyButton';
import { useGuestIdentificationDialogController } from './GuestIdentificationDialog.controller';
import { hasDismissedGuestIdentificationDialog, markGuestIdentificationDialogDismissed } from './guestIdentificationDismissal';
import type { GuestIdentificationStep } from './GuestIdentificationDialog.types';

// null = dismissal state not loaded yet (SSR or before eventId is known).
// true/false = localStorage has been read for the current eventId.
// Distinguishing "unknown" from "not dismissed" prevents the dialog from
// briefly auto-opening on refresh before we've read the persisted flag.
type DismissalState = boolean | null;

const isPreviewing = queryString.getValueString('preview') === 'true';

/**
 * Top-level gate: skips the provider entirely in the admin website editor's
 * nested guest-site iframe, where an upstream eventByName eviction loop would
 * pull our query into a refetch storm on localhost. The narrow condition is
 * "admin viewing inside an iframe" — admins testing the guest site directly
 * (most common dev workflow) still see it; the dialog itself naturally
 * filters out admins-with-identity via canIdentifyGuest.
 *
 * Also skips on explicit preview URLs (?preview=true).
 *
 * Inside an iframe we additionally wait for auth to settle before mounting
 * the inner provider. During the startup window `isAdmin` briefly reports
 * false, so without this guard the eligibility query would subscribe and get
 * pulled into the preview refetch storm before the admin check can resolve.
 * We scope this wait to iframe contexts only — incognito localhost direct
 * visits aren't in an iframe, and there `hasIdentifiedUserOnce` can stay
 * false indefinitely (cross-origin auth probe to dev.withjoy.com).
 */
export const GuestIdentificationDialogProvider: React.FC = ({ children }) => {
  const { isAdmin, hasIdentifiedUserOnce } = useEventUserRole();
  const insideIframe = isInIframe();

  if (isPreviewing || (insideIframe && (!hasIdentifiedUserOnce || isAdmin))) {
    return <>{children}</>;
  }

  return <GuestIdentificationDialogProviderInner>{children}</GuestIdentificationDialogProviderInner>;
};

const GuestIdentificationDialogProviderInner: React.FC = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [step, setStep] = useState<GuestIdentificationStep>('email');
  const [email, setEmail] = useState('');
  const [isDismissed, setIsDismissed] = useState<DismissalState>(null);
  const { canIdentifyGuest, eventId } = useGuestIdentificationDialogController();
  const hasAutoOpenedRef = useRef(false);

  // Read dismissal flag from localStorage once eventId is known. Stays null
  // during SSR and until the eligibility query resolves.
  useEffect(() => {
    if (!eventId) {
      setIsDismissed(null);
      return;
    }
    setIsDismissed(hasDismissedGuestIdentificationDialog(eventId));
  }, [eventId]);

  // Auto-open once per mount when the guest is eligible AND we've confirmed
  // they haven't dismissed it before (isDismissed === false, not null).
  useEffect(() => {
    if (!canIdentifyGuest || isDismissed !== false || hasAutoOpenedRef.current) return;
    hasAutoOpenedRef.current = true;
    onOpen();
  }, [canIdentifyGuest, isDismissed, onOpen]);

  const handleDismiss = useCallback(() => {
    if (!eventId) return;
    markGuestIdentificationDialogDismissed(eventId);
    setIsDismissed(true);
  }, [eventId]);

  const handleStickyButtonClick = useCallback(() => {
    setStep('email');
    onOpen();
  }, [onOpen]);

  // Sticky CTA takes over once the guest has dismissed the dialog. On refresh
  // with a persisted dismissal, this renders directly (with its slide-in
  // animation) instead of the dialog re-appearing.
  const showStickyButton = canIdentifyGuest && isDismissed === true && !isOpen;

  return (
    <GuestIdentificationDialogContextProvider value={{ isOpen, onOpen, onClose, step, setStep, email, setEmail }}>
      {children}
      <GuestIdentificationDialog
        eventId={eventId}
        onDismiss={handleDismiss}
      />
      {showStickyButton && <GuestIdentificationStickyButton onClick={handleStickyButtonClick} />}
    </GuestIdentificationDialogContextProvider>
  );
};
